import { onScopeDispose, type Ref } from 'vue'
import { prefersReducedMotion } from '@consumer/helpers/screen'

type Options = {
  typeMs?: number
  deleteMs?: number
  holdMs?: number
  // Fired each time a fresh example has finished typing. Lets a caller running
  // several of these hand the turn to the next one on a real cycle boundary
  // instead of a timer, which would cut a word mid-erase.
  onCycle?: () => void
}

// Cycles example prompts into a reactive placeholder with a typewriter effect —
// type out, hold, delete, next — pausing whenever `active` is false (i.e. the
// field has content). Honours prefers-reduced-motion by cycling whole strings.
export function useTypewriterPlaceholder (
  examples: string[],
  active: () => boolean,
  { typeMs = 32, deleteMs = 18, holdMs = 1100, onCycle }: Options = {},
): Ref<string> {
  const text = ref(examples[0] ?? '')
  const reduced = prefersReducedMotion()

  let timer: ReturnType<typeof setTimeout> | undefined
  let index = 0
  let pos = examples[0]?.length ?? 0
  let deleting = false
  // Distinguishes "typed a fresh example" from the initial string, which is
  // already complete and shouldn't count as a cycle.
  let retyping = false

  function stop () {
    if (timer) clearTimeout(timer)
    timer = undefined
  }

  function schedule (delay: number) {
    stop()
    timer = setTimeout(step, delay)
  }

  function step () {
    if (!active()) return

    if (reduced) {
      index = (index + 1) % examples.length
      text.value = examples[index]
      onCycle?.()
      schedule(holdMs * 2)
      return
    }

    const current = examples[index]
    if (!deleting) {
      pos += 1
      text.value = current.slice(0, pos)
      if (pos >= current.length) {
        deleting = true
        if (retyping) {
          retyping = false
          onCycle?.()
        }
        schedule(holdMs)
        return
      }
      schedule(typeMs)
    }
    else {
      pos -= 1
      text.value = current.slice(0, pos)
      if (pos <= 0) {
        deleting = false
        retyping = true
        index = (index + 1) % examples.length
      }
      schedule(pos <= 0 ? typeMs : deleteMs)
    }
  }

  watch(active, (on) => {
    if (on) schedule(holdMs)
    else stop()
  }, { immediate: true })

  onScopeDispose(stop)

  return text
}
